import React, { useState } from 'react';
import { rideWebhook } from '../data/ride/webhook';

const sampleHeaders = `POST /your-webhook-endpoint
Content-Type: application/JSON
x-secure-signature: aBcDeFgHiJkLmNoPqRsTuVwXyZ0123456789+/==`;

const details = [
  { label: 'Type',         val: 'Outbound webhook (Wiyak → your server)' },
  { label: 'Method',       val: 'POST' },
  { label: 'Content-Type', val: 'application/JSON' },
  { label: 'Protocol',     val: 'HTTPS only' },
  { label: 'Signature',    val: 'HMAC-SHA256 → Base64' },
  { label: 'Auth Header',  val: 'x-secure-signature' },
];

const signatureDetails = [
  { label: 'Algorithm', val: 'HMAC-SHA256' },
  { label: 'Signed data', val: 'Exact JSON body string' },
  { label: 'Output encoding', val: 'Base64' },
  { label: 'Header name (default)', val: 'x-secure-signature' },
];

const troubleshooting = [
  { problem: 'Signature never matches', cause: 'Body was parsed/rebuilt before verify', fix: 'Use raw body string' },
  { problem: 'Signature never matches', cause: 'Wrong secret key', fix: 'Confirm webhook_secure_key with Wiyak' },
  { problem: 'Header not found', cause: 'Wrong header name', fix: 'Confirm configured header (default x-secure-signature)' },
  { problem: 'Header not found', cause: 'Framework lowercases headers', fix: 'Read as x-secure-signature / X-Secure-Signature correctly' },
  { problem: 'Works in test, fails in prod', cause: 'Different secret between environments', fix: 'Use correct env secret' },
];

const checklist = [
  'Callback URL is HTTPS and publicly reachable',
  'webhook_secure_key is stored securely',
  'Signature header name is confirmed',
  'HMAC-SHA256 + Base64 verification is implemented',
  'Invalid signatures are rejected',
  'Valid webhooks return HTTP 200',
  'Booking status updates are handled for all status values',
];

const bestPractices = [
  'Always verify the signature before processing.',
  'Store webhook_secure_key in environment variables / secret manager.',
  'Use HTTPS for your callback URL.',
  'Reject requests with missing or invalid signature.',
  'Process webhooks idempotently (same booking update may be retried).',
  'Do not log the full secret key.',
  'Prefer raw-body verification to avoid JSON serialize mismatches.',
];

const codeExamples = {
  node: {
    label: 'Node.js (Express)',
    code: `const crypto = require('crypto');
const express = require('express');
const app = express();

const WEBHOOK_SECURE_KEY = process.env.WEBHOOK_SECURE_KEY;
const SIGNATURE_HEADER = 'x-secure-signature'; // confirm with Wiyak if different

// Capture raw body for signature verification
app.use(express.json({
  verify: (req, res, buf) => {
    req.rawBody = buf.toString('utf8');
  }
}));

app.post('/webhook', (req, res) => {
  const receivedSignature = req.headers[SIGNATURE_HEADER];
  if (!receivedSignature) {
    return res.status(401).json({ status: false, message: 'Missing signature' });
  }

  const expectedSignature = crypto
    .createHmac('sha256', WEBHOOK_SECURE_KEY)
    .update(req.rawBody)
    .digest('base64');

  const isValid =
    receivedSignature.length === expectedSignature.length &&
    crypto.timingSafeEqual(
      Buffer.from(receivedSignature),
      Buffer.from(expectedSignature)
    );

  if (!isValid) {
    return res.status(401).json({ status: false, message: 'Invalid signature' });
  }

  // Signature OK — process booking update
  const payload = req.body;
  console.log('Webhook verified:', payload.status, payload.Booking_id);

  return res.status(200).json({ status: true });
});`,
  },
  php: {
    label: 'PHP',
    code: `<?php
$webhookSecureKey = getenv('WEBHOOK_SECURE_KEY');
$headerName = 'HTTP_X_SECURE_SIGNATURE'; // maps to x-secure-signature

$receivedSignature = $_SERVER[$headerName] ?? '';
$rawBody = file_get_contents('php://input');

$expectedSignature = base64_encode(
    hash_hmac('sha256', $rawBody, $webhookSecureKey, true)
);

if (!hash_equals($expectedSignature, $receivedSignature)) {
    http_response_code(401);
    echo json_encode(['status' => false, 'message' => 'Invalid signature']);
    exit;
}

$payload = json_decode($rawBody, true);
// Process booking update...

http_response_code(200);
echo json_encode(['status' => true]);`,
  },
  python: {
    label: 'Python (Flask)',
    code: `import os
import hmac
import hashlib
import base64
from flask import Flask, request, jsonify

app = Flask(__name__)

WEBHOOK_SECURE_KEY = os.environ["WEBHOOK_SECURE_KEY"]
SIGNATURE_HEADER = "X-Secure-Signature"  # confirm with Wiyak if different

@app.post("/webhook")
def webhook():
    received = request.headers.get(SIGNATURE_HEADER, "")
    raw_body = request.get_data()  # raw bytes

    digest = hmac.new(
        WEBHOOK_SECURE_KEY.encode("utf-8"),
        raw_body,
        hashlib.sha256
    ).digest()
    expected = base64.b64encode(digest).decode("utf-8")

    if not hmac.compare_digest(expected, received):
        return jsonify({"status": False, "message": "Invalid signature"}), 401

    payload = request.get_json()
    # Process booking update...
    return jsonify({"status": True}), 200`,
  },
  csharp: {
    label: 'C# / .NET',
    code: `using System.Security.Cryptography;
using System.Text;
using System.Text.Json;
using Microsoft.AspNetCore.Mvc;

[ApiController]
[Route("webhook")]
public class WebhookController : ControllerBase
{
    private static readonly string WebhookSecureKey =
        Environment.GetEnvironmentVariable("WEBHOOK_SECURE_KEY")!;
    private const string SignatureHeader = "x-secure-signature"; // confirm with Wiyak if different

    [HttpPost]
    public async Task<IActionResult> Receive()
    {
        if (!Request.Headers.TryGetValue(SignatureHeader, out var receivedSignature) ||
            string.IsNullOrEmpty(receivedSignature))
        {
            return Unauthorized(new { status = false, message = "Missing signature" });
        }

        using var reader = new StreamReader(Request.Body, Encoding.UTF8);
        var rawBody = await reader.ReadToEndAsync();

        using var hmac = new HMACSHA256(Encoding.UTF8.GetBytes(WebhookSecureKey));
        var hash = hmac.ComputeHash(Encoding.UTF8.GetBytes(rawBody));
        var expected = Convert.ToBase64String(hash);

        if (!CryptographicOperations.FixedTimeEquals(
                Encoding.UTF8.GetBytes(expected),
                Encoding.UTF8.GetBytes(receivedSignature!)))
        {
            return Unauthorized(new { status = false, message = "Invalid signature" });
        }

        // Signature OK — process booking update
        using var doc = JsonDocument.Parse(rawBody);
        // var status = doc.RootElement.GetProperty("status").GetString();

        return Ok(new { status = true });
    }
}`,
  },
  java: {
    label: 'Java',
    code: `import javax.crypto.Mac;
import javax.crypto.spec.SecretKeySpec;
import java.nio.charset.StandardCharsets;
import java.security.MessageDigest;
import java.util.Base64;
import org.springframework.http.ResponseEntity;
import org.springframework.web.bind.annotation.*;

@RestController
public class WebhookController {

    private static final String WEBHOOK_SECURE_KEY = System.getenv("WEBHOOK_SECURE_KEY");
    private static final String SIGNATURE_HEADER = "x-secure-signature"; // confirm with Wiyak if different

    @PostMapping("/webhook")
    public ResponseEntity<?> webhook(
            @RequestHeader(value = SIGNATURE_HEADER, required = false) String receivedSignature,
            @RequestBody String rawBody) throws Exception {

        if (receivedSignature == null || receivedSignature.isEmpty()) {
            return ResponseEntity.status(401)
                .body("{\\"status\\":false,\\"message\\":\\"Missing signature\\"}");
        }

        Mac mac = Mac.getInstance("HmacSHA256");
        mac.init(new SecretKeySpec(
            WEBHOOK_SECURE_KEY.getBytes(StandardCharsets.UTF_8), "HmacSHA256"));
        byte[] digest = mac.doFinal(rawBody.getBytes(StandardCharsets.UTF_8));
        String expected = Base64.getEncoder().encodeToString(digest);

        if (!MessageDigest.isEqual(
                expected.getBytes(StandardCharsets.UTF_8),
                receivedSignature.getBytes(StandardCharsets.UTF_8))) {
            return ResponseEntity.status(401)
                .body("{\\"status\\":false,\\"message\\":\\"Invalid signature\\"}");
        }

        // Signature OK — process booking update (parse rawBody as JSON)
        return ResponseEntity.ok("{\\"status\\":true}");
    }
}`,
  },
};

const sectionTitle = {
  fontSize: 18,
  fontWeight: 700,
  color: 'var(--text-primary)',
  marginBottom: 14,
  letterSpacing: '-0.02em',
};

const codeInline = {
  color: 'var(--accent-light)',
  background: 'var(--accent-bg)',
  padding: '2px 7px',
  borderRadius: 5,
  fontFamily: 'JetBrains Mono, monospace',
  fontSize: 13,
};

function CodeWindow({ label, children }) {
  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 14, overflow: 'hidden', background: 'var(--code-bg)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '8px 16px', background: 'var(--code-header)', borderBottom: '1px solid var(--border)', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 6 }}>
          {['#ef4444', '#f59e0b', '#22c55e'].map((c, i) => (
            <div key={i} style={{ width: 10, height: 10, borderRadius: '50%', background: c, opacity: 0.7 }} />
          ))}
        </div>
        <span style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'JetBrains Mono, monospace' }}>{label}</span>
        <div />
      </div>
      <pre style={{ margin: 0, padding: '18px 20px', fontSize: 12.5, color: 'var(--syn-string)', fontFamily: 'JetBrains Mono, monospace', lineHeight: 1.75, overflowX: 'auto' }}>
        {children}
      </pre>
    </div>
  );
}

function TwoColTable({ headers, rows }) {
  return (
    <div className="status-table-wrap webhook-table-2">
      <div className="status-table-head webhook-table-2-row">
        {headers.map((h) => (
          <div key={h} style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.07em' }}>{h}</div>
        ))}
      </div>
      {rows.map((row, i) => (
        <div
          key={i}
          className="status-table-row webhook-table-2-row"
          style={{ borderBottom: i < rows.length - 1 ? '1px solid var(--border-subtle)' : 'none' }}
        >
          {row}
        </div>
      ))}
    </div>
  );
}

export default function WebhookPage({ webhook = rideWebhook }) {
  const [lang, setLang] = useState('node');
  const activeExample = codeExamples[lang];
  const payload = webhook.payload || rideWebhook.payload;
  const payloadFields = webhook.payloadFields || rideWebhook.payloadFields;
  const statusValues = webhook.statusValues || rideWebhook.statusValues;
  const credentials = webhook.credentials || rideWebhook.credentials;

  return (
    <div className="page-content">

      {/* Header */}
      <div style={{ marginBottom: 36 }}>
        <h1 className="page-title">Webhook Reference</h1>
        <p style={{ fontSize: 15, color: 'var(--text-secondary)', lineHeight: 1.75, maxWidth: 640, marginBottom: 10 }}>
          When a booking status changes, Wiyak sends a webhook <code style={codeInline}>POST</code> to your registered callback URL.
          Every request includes an <strong style={{ color: 'var(--text-primary)' }}>HMAC-SHA256</strong> signature so you can prove it came from Wiyak.
        </p>
        <p style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.6 }}>
          Type: Outbound webhook (Wiyak → your server) · Content-Type: <code style={{ ...codeInline, fontSize: 12 }}>application/JSON</code>
        </p>
      </div>

      <div className="grid-2" style={{ gap: 20, marginBottom: 32 }}>
        {/* Setup */}
        <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 14, padding: 20, minWidth: 0 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 12 }}>Setup</div>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: 14 }}>
            Pass your webhook URL during login. It must use <strong style={{ color: 'var(--text-primary)' }}>HTTPS</strong>. You only need to set it once.
          </p>
          <div style={{ background: 'var(--code-bg)', border: '1px solid var(--border)', borderRadius: 10, padding: '12px 14px', overflow: 'hidden' }}>
            <pre style={{ margin: 0, fontSize: 12, color: 'var(--syn-string)', fontFamily: 'JetBrains Mono, monospace', lineHeight: 1.7, overflowX: 'auto' }}>
{`POST /corporate/authorize
Header: bearerkey: xxx@786101

{
  "web_hook_url":
    "https://yourapp.com/callback"
}`}
            </pre>
          </div>
        </div>

        {/* Request Details */}
        <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 14, padding: 20, minWidth: 0 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 12 }}>Request Details</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {details.map((d, i) => (
              <div key={i} className="webhook-detail-row">
                <span style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 500 }}>{d.label}</span>
                <code style={{ fontSize: 12, color: 'var(--text-primary)', fontFamily: 'JetBrains Mono, monospace', wordBreak: 'break-all' }}>{d.val}</code>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Overview */}
      <div style={{ marginBottom: 32 }}>
        <h2 style={sectionTitle}>Overview</h2>
        <p style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.7, marginBottom: 14, maxWidth: 640 }}>
          To prove the request is from Wiyak (and not a third party), every webhook includes a HMAC-SHA256 signature in a custom HTTP header.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {[
            'Receive the webhook body',
            'Recompute the signature using your shared secret',
            'Compare it with the signature header',
            'Accept only if they match',
          ].map((step, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 10, padding: '12px 14px' }}>
              <span style={{
                width: 24, height: 24, borderRadius: 8, flexShrink: 0,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 12, fontWeight: 700, fontFamily: 'JetBrains Mono, monospace',
                background: 'var(--accent-bg)', color: 'var(--accent)', border: '1px solid var(--accent-glow)',
              }}>{i + 1}</span>
              <span style={{ fontSize: 13, color: 'var(--text-primary)', lineHeight: 1.5 }}>{step}</span>
            </div>
          ))}
        </div>
      </div>

      {/* What you need */}
      <div style={{ marginBottom: 32 }}>
        <h2 style={sectionTitle}>What you need from Wiyak</h2>
        <TwoColTable
          headers={['Item', 'Description']}
          rows={credentials.map((c) => [
            <code key="i" style={{ fontSize: 12.5, color: 'var(--accent-light)', fontFamily: 'JetBrains Mono, monospace' }}>{c.item}</code>,
            <span key="d" style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.55 }}>{c.desc}</span>,
          ])}
        />
        <div style={{
          marginTop: 14, background: 'var(--accent-bg)', border: '1px solid var(--accent-glow)',
          borderRadius: 12, padding: '14px 18px', borderLeft: '3px solid var(--accent)',
        }}>
          <p style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
            Keep <code style={codeInline}>webhook_secure_key</code> private. Never expose it in frontend apps, public repos, or client-side code.
          </p>
        </div>
      </div>

      {/* How signature is generated */}
      <div style={{ marginBottom: 32 }}>
        <h2 style={sectionTitle}>How the signature is generated</h2>
        <p style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.7, marginBottom: 14 }}>
          Wiyak generates the signature like this:
        </p>
        <CodeWindow label="Signature formula">
{`signature = Base64( HMAC-SHA256( webhook_secure_key, JSON.stringify(payload) ) )`}
        </CodeWindow>
        <div style={{ marginTop: 14, marginBottom: 14 }}>
          <CodeWindow label="Outbound request">{sampleHeaders}{'\n\n'}{`{ ... JSON payload ... }`}</CodeWindow>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {signatureDetails.map((d, i) => (
            <div key={i} className="webhook-detail-row">
              <span style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 500 }}>{d.label}</span>
              <code style={{ fontSize: 12, color: 'var(--text-primary)', fontFamily: 'JetBrains Mono, monospace' }}>{d.val}</code>
            </div>
          ))}
        </div>
      </div>

      {/* How to verify */}
      <div style={{ marginBottom: 32 }}>
        <h2 style={sectionTitle}>How to verify</h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
          {[
            'Read the raw request body as a string (recommended).',
            'Read the signature from the header (x-secure-signature or your configured header name).',
            'Compute: expected = Base64( HMAC-SHA256( webhook_secure_key, raw_body ) )',
            'Compare expected with the received header value.',
            'If match → process webhook. If not → reject with 401 or 403.',
          ].map((step, i) => (
            <div key={i} style={{ display: 'flex', gap: 12, background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 10, padding: '12px 14px' }}>
              <span style={{
                width: 24, height: 24, borderRadius: 8, flexShrink: 0,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 12, fontWeight: 700, fontFamily: 'JetBrains Mono, monospace',
                background: 'var(--accent-bg)', color: 'var(--accent)', border: '1px solid var(--accent-glow)',
              }}>{i + 1}</span>
              <span style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.55 }}>{step}</span>
            </div>
          ))}
        </div>
        <div style={{
          background: 'var(--accent-bg)', border: '1px solid var(--accent-glow)',
          borderRadius: 12, padding: '16px 20px', borderLeft: '3px solid var(--accent)',
        }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 10 }}>
            Important
          </div>
          {[
            'Prefer verifying against the raw body string, not a re-serialized JSON object.',
            'Do not pretty-print or reorder JSON keys before verifying.',
            'Use a constant-time compare when possible (hash_equals, hmac.compare_digest, etc.).',
          ].map((note, i) => (
            <div key={i} style={{ display: 'flex', gap: 8, marginBottom: i < 2 ? 7 : 0, fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.55 }}>
              <span style={{ color: 'var(--accent)', flexShrink: 0 }}>↗</span>
              {note}
            </div>
          ))}
        </div>
      </div>

      {/* Verification examples */}
      <div style={{ marginBottom: 32 }}>
        <h2 style={sectionTitle}>Verification examples</h2>
        <div className="status-filter" style={{ marginBottom: 14 }}>
          {Object.entries(codeExamples).map(([key, ex]) => {
            const isActive = lang === key;
            return (
              <button
                key={key}
                type="button"
                onClick={() => setLang(key)}
                style={{
                  padding: '6px 16px', borderRadius: 7, border: 'none', cursor: 'pointer',
                  fontSize: 13, fontWeight: 500, fontFamily: 'Inter, sans-serif',
                  background: isActive ? 'var(--bg-base)' : 'transparent',
                  color: isActive ? 'var(--text-primary)' : 'var(--text-muted)',
                  boxShadow: isActive ? 'var(--shadow-sm)' : 'none',
                  transition: 'all 0.15s',
                }}
              >
                {ex.label}
              </button>
            );
          })}
        </div>
        <CodeWindow label={activeExample.label}>{activeExample.code}</CodeWindow>
      </div>

      {/* Sample request */}
      <div style={{ marginBottom: 32 }}>
        <h2 style={sectionTitle}>Sample webhook request</h2>
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 8 }}>Headers</div>
          <CodeWindow label="HTTP headers">{sampleHeaders}</CodeWindow>
        </div>
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 8 }}>Body</div>
          <CodeWindow label="Webhook Payload · application/json">{payload}</CodeWindow>
        </div>
      </div>

      {/* Payload fields */}
      <div style={{ marginBottom: 32 }}>
        <h2 style={sectionTitle}>Payload fields</h2>
        <div className="status-table-wrap webhook-table-3">
          <div className="status-table-head webhook-table-3-row">
            {['Field', 'Type', 'Description'].map((h) => (
              <div key={h} style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.07em' }}>{h}</div>
            ))}
          </div>
          {payloadFields.map((f, i) => (
            <div
              key={f.field}
              className="status-table-row webhook-table-3-row"
              style={{ borderBottom: i < payloadFields.length - 1 ? '1px solid var(--border-subtle)' : 'none' }}
            >
              <code style={{ fontSize: 12.5, color: 'var(--accent-light)', fontFamily: 'JetBrains Mono, monospace' }}>{f.field}</code>
              <span style={{ fontSize: 12, color: 'var(--text-muted)', fontFamily: 'JetBrains Mono, monospace' }}>{f.type}</span>
              <span style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.55 }}>{f.desc}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Status values */}
      <div style={{ marginBottom: 32 }}>
        <h2 style={sectionTitle}>Status values</h2>
        <TwoColTable
          headers={['Status', 'Meaning']}
          rows={statusValues.map((s) => [
            <code key="s" style={{ fontSize: 12.5, color: 'var(--accent-light)', fontFamily: 'JetBrains Mono, monospace' }}>{s.status}</code>,
            <span key="m" style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.55 }}>{s.meaning}</span>,
          ])}
        />
      </div>

      {/* Expected response */}
      <div style={{ marginBottom: 32 }}>
        <h2 style={sectionTitle}>Expected response from your server</h2>
        <p style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.7, marginBottom: 14 }}>
          Return HTTP <code style={codeInline}>200</code> quickly after validating the signature. Use HTTP <code style={codeInline}>401</code> or <code style={codeInline}>403</code> for invalid/missing signature.
        </p>
        <div className="grid-2" style={{ gap: 16 }}>
          <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--success-border)', borderRadius: 12, overflow: 'hidden', minWidth: 0 }}>
            <div style={{ padding: '8px 14px', background: 'var(--success-bg)', borderBottom: '1px solid var(--success-border)', fontSize: 12, fontWeight: 600, color: 'var(--success)' }}>
              Success (HTTP 200)
            </div>
            <pre style={{ margin: 0, padding: '14px 16px', fontSize: 12, lineHeight: 1.7, color: 'var(--success)', fontFamily: 'JetBrains Mono, monospace', overflowX: 'auto' }}>
{`{
  "status": true
}`}
            </pre>
          </div>
          <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--error-border)', borderRadius: 12, overflow: 'hidden', minWidth: 0 }}>
            <div style={{ padding: '8px 14px', background: 'var(--error-bg)', borderBottom: '1px solid var(--error-border)', fontSize: 12, fontWeight: 600, color: 'var(--error)' }}>
              Invalid signature (HTTP 401 / 403)
            </div>
            <pre style={{ margin: 0, padding: '14px 16px', fontSize: 12, lineHeight: 1.7, color: 'var(--error)', fontFamily: 'JetBrains Mono, monospace', overflowX: 'auto' }}>
{`{
  "status": false,
  "message": "Invalid signature"
}`}
            </pre>
          </div>
        </div>
      </div>

      {/* Security best practices */}
      <div style={{ marginBottom: 32 }}>
        <h2 style={sectionTitle}>Security best practices</h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {bestPractices.map((item, i) => (
            <div key={i} style={{ display: 'flex', gap: 12, background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 10, padding: '12px 14px' }}>
              <span style={{
                width: 24, height: 24, borderRadius: 8, flexShrink: 0,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 12, fontWeight: 700, fontFamily: 'JetBrains Mono, monospace',
                background: 'var(--accent-bg)', color: 'var(--accent)', border: '1px solid var(--accent-glow)',
              }}>{i + 1}</span>
              <span style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.55 }}>{item}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Troubleshooting */}
      <div style={{ marginBottom: 32 }}>
        <h2 style={sectionTitle}>Troubleshooting</h2>
        <div className="status-table-wrap webhook-table-3">
          <div className="status-table-head webhook-table-3-row">
            {['Problem', 'Likely cause', 'Fix'].map((h) => (
              <div key={h} style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.07em' }}>{h}</div>
            ))}
          </div>
          {troubleshooting.map((t, i) => (
            <div
              key={i}
              className="status-table-row webhook-table-3-row"
              style={{ borderBottom: i < troubleshooting.length - 1 ? '1px solid var(--border-subtle)' : 'none' }}
            >
              <span style={{ fontSize: 13, color: 'var(--text-primary)', fontWeight: 500, lineHeight: 1.5 }}>{t.problem}</span>
              <span style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.55 }}>{t.cause}</span>
              <span style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.55 }}>{t.fix}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Checklist */}
      <div style={{ marginBottom: 32 }}>
        <h2 style={sectionTitle}>Quick checklist for vendors</h2>
        <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 14, padding: 18 }}>
          {checklist.map((item, i) => (
            <div key={i} style={{ display: 'flex', gap: 10, marginBottom: i < checklist.length - 1 ? 10 : 0, fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.55 }}>
              <span style={{
                width: 18, height: 18, borderRadius: 5, flexShrink: 0, marginTop: 1,
                border: '1.5px solid var(--border)', background: 'var(--bg-elevated)',
              }} />
              {item}
            </div>
          ))}
        </div>
      </div>

      {/* Support */}
      <div style={{
        background: 'var(--accent-bg)', border: '1px solid var(--accent-glow)',
        borderRadius: 12, padding: '16px 20px', borderLeft: '3px solid var(--accent)',
      }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 10 }}>
          Support
        </div>
        <p style={{ margin: '0 0 10px', fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
          If signature verification fails after following this guide, share with Wiyak support:
        </p>
        {[
          'Your corporate account / company ID',
          'Sample request headers (mask secrets)',
          'Sample raw body',
          'The expected signature you computed (not your secret key)',
        ].map((note, i) => (
          <div key={i} style={{ display: 'flex', gap: 8, marginBottom: i < 3 ? 7 : 0, fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.55 }}>
            <span style={{ color: 'var(--accent)', flexShrink: 0 }}>↗</span>
            {note}
          </div>
        ))}
      </div>
    </div>
  );
}
